import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { FlatOrgTreeTableNode } from 'src/utils/treeNode';
import { ThreeDots } from 'src/components/CommonMenu/comps';
import Menu from '@mui/material/Menu';
import { useAuth } from 'src/state/auth';
import useWorkspaceSwitch from 'src/hooks/useWorkspaceSwitch';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { LSKEY_INITAIL_URL } from 'src/utils/auth';
import { useCache } from 'src/graphql/useCache';
import {
  useAcceptInviteMutation,
  useArchiveWorkspaceMutation,
  useCancelRequestMutation,
  useDeleteWorkspaceMutation,
  useRequestAccessMutation,
  useUnarchiveWorkspaceMutation,
} from 'src/graphql/_generated';
import { useNavigate } from 'react-router-dom';
import { Poppins } from 'src/common';
import { AddUserModal } from 'src/components/modals/AddUserModal';

const StyledMenu = styled(Menu)`
  transform: translateY(10px);
  & .MuiPaper-root {
    border-radius: 3px;
    box-shadow: 0px 0px 37px ${({ theme }) => theme.colors.shadow};
    background-color: ${({ theme }) => theme.colors.white};
  }
`;

const MenuItem = styled.li<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  padding: 4px 10px;
  list-style: none;

  span {
    font-weight: 500;
    color: ${({ theme }) => theme.colors.cflowerBlue};

    ${({ $disabled }) =>
      $disabled &&
      css`
        opacity: 0.5;
        cursor: not-allowed;
      `}

    ${({ $disabled }) =>
      !$disabled &&
      css`
        &:hover {
          background: ${({ theme }) => theme.colors.gradient};
          -webkit-background-clip: text;
          color: transparent;
          cursor: pointer;
        }
      `}
  }
`;

interface TdmProps {
  data: FlatOrgTreeTableNode['data'];
  rowIndex?: number;
}

type Action =
  | 'WORKSPACE_OPEN'
  | 'WORKSPACE_ACCEPT_INVITE'
  | 'WORKSPACE_ACCESS_REQUEST'
  | 'WORKSPACE_ACCESS_CANCEL'
  | 'WORKSPACE_ARCHIVE'
  | 'WORKSPACE_UNARCHIVE'
  | 'WORKSPACE_DELETE'
  | 'WORKSPACE_SETTINGS'
  | 'ORG_ACCESS_REQUEST'
  | 'ORG_SETTINGS'
  | 'INVITE_USERS';

export const Tdm: React.FC<TdmProps> = ({ data, rowIndex }) => {
  const { profile, isPlatformAdmin } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [availableActions, setAvailableActions] = useState<Action[]>([]);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const navigate = useNavigate();

  const { switchOrg, isLoading: isLoadingSwitch } = useWorkspaceSwitch();
  const { refetchQuery } = useCache();

  const resetCache = async () => await refetchQuery({ include: ['profile', 'flatOrganisations'] });

  const [requestAccess, { loading: isLoadingRequestAccess }] = useRequestAccessMutation({
    onCompleted: resetCache,
  });

  const [cancelRequest, { loading: isLoadingCancelRequest }] = useCancelRequestMutation({
    onCompleted: resetCache,
  });

  const [acceptInvite, { loading: isLoadingAcceptInvite }] = useAcceptInviteMutation({
    onCompleted: resetCache,
  });

  const [archiveWorkspace, { loading: isArchiving }] = useArchiveWorkspaceMutation({
    variables: { id: data.id },
    onCompleted: resetCache,
  });

  const [unarchiveWorkspace, { loading: isUnarchiving }] = useUnarchiveWorkspaceMutation({
    variables: { id: data.id },
    onCompleted: resetCache,
  });

  const [deleteWorkspace, { loading: isDeleting }] = useDeleteWorkspaceMutation({
    variables: { id: data.id },
    onCompleted: () => (window.location.href = '/home'),
  });

  const isLoading =
    isLoadingSwitch ||
    isLoadingRequestAccess ||
    isLoadingCancelRequest ||
    isLoadingAcceptInvite ||
    isArchiving ||
    isUnarchiving ||
    isDeleting;

  useEffect(() => {
    const actions: Action[] = [];
    const actionsOnThisOrgUnit = data.actionsOnThisOrgUnit;

    if (data.recordType === 'W') {
      if (profile?.user.requests.some((el) => el.workspaceId === data.id)) {
        actions.push('WORKSPACE_ACCESS_CANCEL');
      } else if (profile?.user.invites.some((el) => el.workspaceId === data.id)) {
        actions.push('WORKSPACE_ACCEPT_INVITE');
      } else if (actionsOnThisOrgUnit?.canAccess || isPlatformAdmin) {
        actions.push('WORKSPACE_OPEN');
      } else if (actionsOnThisOrgUnit?.canRequest) {
        actions.push('WORKSPACE_ACCESS_REQUEST');
      }

      if (actionsOnThisOrgUnit?.canSeeSettings) {
        actions.push('WORKSPACE_SETTINGS');
      }

      if (actionsOnThisOrgUnit?.canArchive) {
        actions.push('WORKSPACE_ARCHIVE');
      }
      if (actionsOnThisOrgUnit?.canUnarchive) {
        actions.push('WORKSPACE_UNARCHIVE');
      }
      if (actionsOnThisOrgUnit?.canDelete) {
        actions.push('WORKSPACE_DELETE');
      }
    }

    if (data.recordType === 'O') {
      if (actionsOnThisOrgUnit?.canSeeSettings) {
        actions.push('ORG_SETTINGS');
      }
      if (actionsOnThisOrgUnit?.canRequest) {
        actions.push('ORG_ACCESS_REQUEST');
      }
    }

    if (actionsOnThisOrgUnit?.canInviteUsers) {
      actions.push('INVITE_USERS');
    }

    setAvailableActions(actions);
  }, [data, isPlatformAdmin, profile]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (e: React.MouseEvent<HTMLLIElement>, action: Action) => {
    e.stopPropagation();
    handleClose();

    switch (action) {
      case 'WORKSPACE_OPEN':
        window.localStorage.setItem(LSKEY_INITAIL_URL, '/risk-assessments');
        switchOrg(data.id);
        mpEvent(MPEvents.WorkspaceSwitch, { workspaceID: data.id });
        break;

      case 'WORKSPACE_ACCESS_CANCEL':
        cancelRequest({ variables: { workspaceId: data.id } });
        mpEvent(MPEvents.ButtonClick, { button: 'OrgTable:CancelRequest' });
        break;

      case 'WORKSPACE_ACCEPT_INVITE':
        acceptInvite({
          variables: { inviteId: profile?.user.invites.find((el) => el.workspaceId === data.id)?.id! },
        });
        mpEvent(MPEvents.ButtonClick, { button: 'OrgTable:AcceptInvite' });
        break;

      case 'WORKSPACE_ACCESS_REQUEST':
        requestAccess({ variables: { workspaceId: data.id } });
        mpEvent(MPEvents.ButtonClick, { button: 'OrgTable:RequestAccess' });
        break;

      case 'WORKSPACE_ARCHIVE':
        archiveWorkspace();
        mpEvent(MPEvents.ButtonClick, { button: 'OrgTable:ArchiveWorkspace' });
        break;

      case 'WORKSPACE_UNARCHIVE':
        unarchiveWorkspace();
        mpEvent(MPEvents.ButtonClick, { button: 'OrgTable:UnarchiveWorkspace' });
        break;

      case 'WORKSPACE_DELETE':
        deleteWorkspace();
        mpEvent(MPEvents.ButtonClick, { button: 'OrgTable:DeleteWorkspace' });
        break;

      case 'WORKSPACE_SETTINGS':
        navigate(`/workspace/${data.id}`);
        mpEvent(MPEvents.ButtonClick, { button: 'OrgTable:ViewWorkspaceSettings' });
        break;

      case 'ORG_ACCESS_REQUEST':
        requestAccess({ variables: { organisationId: data.id } });
        mpEvent(MPEvents.ButtonClick, { button: 'OrgTable:RequestAccess' });
        break;

      case 'ORG_SETTINGS':
        navigate(`/organisation/${data.id}`);
        mpEvent(MPEvents.ButtonClick, { button: 'OrgTable:ViewOrganisationSettings' });
        break;

      case 'INVITE_USERS':
        setAddUserModalOpen(true);
        mpEvent(MPEvents.ButtonClick, { button: 'OrgTable:InviteUsers' });
        break;
    }
  };

  const getActionLabel = (action: Action): string => {
    switch (action) {
      case 'WORKSPACE_OPEN':
        return 'Open';
      case 'WORKSPACE_ACCESS_CANCEL':
        return 'Cancel request';
      case 'WORKSPACE_ACCEPT_INVITE':
        return 'Accept invite';
      case 'WORKSPACE_ACCESS_REQUEST':
        return 'Request access';
      case 'WORKSPACE_ARCHIVE':
        return 'Archive';
      case 'WORKSPACE_UNARCHIVE':
        return 'Unarchive';
      case 'WORKSPACE_DELETE':
        return 'Delete';
      case 'WORKSPACE_SETTINGS':
        return 'View settings';
      case 'ORG_ACCESS_REQUEST':
        return 'Request access';
      case 'ORG_SETTINGS':
        return 'View settings';
      case 'INVITE_USERS':
        return 'Invite users';
    }
  };

  if (availableActions.length === 0) {
    return null;
  }

  return (
    <>
      {addUserModalOpen && (
        <AddUserModal
          id={data.id}
          type={data.recordType === 'W' ? 'workspace' : 'organisation'}
          onClose={() => setAddUserModalOpen(false)}
          emailsInUse={[]}
          initialValues={{ name: '', email: '' }}
        />
      )}
      <div onClick={(e) => e.stopPropagation()} style={{ marginLeft: '4px' }} data-cy={`tdm-wrapper-${rowIndex}`}>
        <ThreeDots onClick={handleClick} alwaysActiveColor dataCy={`tdm-button-${rowIndex}`} />
        <StyledMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          autoFocus={false}
        >
          <div>
            {availableActions.map((action) => (
              <MenuItem
                key={action}
                $disabled={isLoading}
                onClick={(e) => !isLoading && handleMenuItemClick(e, action)}
                data-cy={`tdm-menu-item-${action.toLowerCase().replace(/_/g, '-')}`}
              >
                <Poppins px={16}>{getActionLabel(action)}</Poppins>
              </MenuItem>
            ))}
          </div>
        </StyledMenu>
      </div>
    </>
  );
};
